import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Loader2, Send, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/tele`;

function StartForm({ onStarted }) {
  const { user } = useAuth();
  const people = [{ id: "self", full_name: user.full_name, relationship: "SELF" }, ...(user.beneficiaries || [])];
  const [f, setF] = useState({ beneficiary_id: "self", age: "", address: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try { onStarted((await api.post("/tele/consults", { ...f, age: Number(f.age) })).data); } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="surface space-y-3 p-5" data-testid="gia-start-form">
      <p className="text-sm text-slate-600">Tell GIA who the patient is. A GIA doctor reviews your answers and can send an e-RX without a clinic visit.</p>
      <Field label="Patient" hint={<>Only you or family registered in your <Link to="/app/profile" className="font-semibold text-purple-700 underline">profile</Link></>}>
        <select className="field" value={f.beneficiary_id} onChange={set("beneficiary_id")} required data-testid="gia-patient-select">
          {people.map((p) => <option key={p.id} value={p.id}>{p.full_name} · {p.relationship === "SELF" ? "Me" : p.relationship.charAt(0) + p.relationship.slice(1).toLowerCase()}</option>)}
        </select>
      </Field>
      <Field label="Age"><input type="number" min={0} max={130} className="field" value={f.age} onChange={set("age")} required data-testid="gia-patient-age" /></Field>
      <Field label="Address"><input className="field" value={f.address} onChange={set("address")} required minLength={3} data-testid="gia-patient-address" /></Field>
      <ErrorBox error={error} />
      <button className="btn-primary w-full py-3" data-testid="gia-start-button"><Bot size={16} /> Start with GIA</button>
    </form>
  );
}

function Chat({ tc, setTc, onSubmitted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [tc.messages]);
  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || busy) return;
    setText(""); setBusy(true);
    let reply = "";
    setTc((t) => ({ ...t, messages: [...t.messages, { role: "user", content: msg }, { role: "assistant", content: "" }] }));
    try {
      const post = () => fetch(`${BASE}/consults/${tc.id}/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: msg }) });
      let res = await post();
      if (res.status === 401) { await api.post("/auth/refresh"); res = await post(); }
      if (!res.ok) throw new Error((await res.json()).detail || "GIA is unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const c of chunks) {
          const data = c.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          const ev = JSON.parse(data);
          if (ev.error) toast.error(ev.error);
          if (ev.delta) { reply += ev.delta; setTc((t) => ({ ...t, messages: [...t.messages.slice(0, -1), { role: "assistant", content: reply }] })); }
        }
      }
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!window.confirm("Send your answers to a GIA doctor for review?")) return;
    setBusy(true);
    try { setTc((await api.post(`/tele/consults/${tc.id}/submit`)).data); toast.success("Sent to the doctor"); onSubmitted(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface flex h-[65vh] flex-col p-3" data-testid="gia-chat">
      <div className="flex-1 space-y-3 overflow-y-auto p-2">
        {tc.messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-purple-600 text-white" : "bg-slate-100 text-slate-800"}`} data-testid={`gia-msg-${i}`}>
            {m.content || <Loader2 size={14} className="animate-spin" />}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-slate-100 pt-3">
        <input className="field" placeholder="Describe your symptoms…" value={text} onChange={(e) => setText(e.target.value)} disabled={busy} data-testid="gia-input" />
        <button className="btn-primary" disabled={busy || !text.trim()} data-testid="gia-send-button"><Send size={15} /></button>
      </form>
      <button className="btn-secondary mt-2 w-full" disabled={busy || tc.messages.length < 3} onClick={submit} data-testid="gia-submit-button"><Stethoscope size={15} /> Send to doctor</button>
      <p className="mt-1 text-center text-[10px] text-slate-400">GIA does not diagnose. In an emergency go to the nearest ER or call 911.</p>
    </div>
  );
}

const Outcome = ({ tc }) => (
  <div className="surface space-y-2 p-5" data-testid="gia-outcome">
    <div className="flex items-center justify-between"><p className="font-bold">{tc.patient.name}</p><StatusBadge status={tc.status} /></div>
    {tc.status === "SUBMITTED" && <p className="text-sm text-slate-600">A GIA doctor is reviewing your request. You'll get an email and see the e-RX under Records once approved.</p>}
    {tc.status === "APPROVED" && <Link to={`/print/rx/${tc.rx_number}`} className="btn-primary w-full" data-testid="gia-view-rx">View e-RX {tc.rx_number}</Link>}
    {tc.status === "DECLINED" && <p className="text-sm text-red-700">Doctor's note: {tc.decline_reason}. Please book a clinic visit.</p>}
  </div>
);

export default function AskGia() {
  const [tc, setTc] = useState(null);
  const list = useFetch("/tele/consults");
  const open = async (id) => { try { setTc((await api.get(`/tele/consults/${id}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Remote consultation" title="Ask GIA" sub="Describe your symptoms. A GIA doctor reviews them and may issue an e-RX — no clinic visit needed." />
      {!tc ? <StartForm onStarted={(t) => { setTc(t); list.reload(); }} /> : tc.status === "DRAFT" ? <Chat tc={tc} setTc={setTc} onSubmitted={list.reload} /> : <Outcome tc={tc} />}
      {tc && <button className="btn-ghost btn-sm" onClick={() => { setTc(null); list.reload(); }} data-testid="gia-new-button">Start another</button>}
      {!list.data ? <Spinner /> : list.data.length > 0 && (
        <div className="space-y-2" data-testid="gia-history">
          <p className="label">My remote consultations</p>
          {list.data.map((t) => (
            <button key={t.id} onClick={() => open(t.id)} className="surface flex w-full items-center justify-between p-3 text-left" data-testid={`gia-history-${t.id}`}>
              <span className="text-sm"><b>{t.patient.name}</b><br /><span className="text-xs text-slate-500">{fmtDateTime(t.created_at)}</span></span><StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
